import { noop } from "lodash/fp";

import { PARTICIPANT_ACCESS_OPTIONS } from "@app/analytics/constants/sharing";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import useAuth from "@app/common/auth/useAuth";
import type { ParticipantAccessLevel, UserSettings } from "@app/common/types";
import * as UserSettingsUtils from "@app/common/utils/userSettingsUtils";

import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import TooltipWrapper from "@rui/TooltipWrapper";

import { SETTING_COPY } from "./constants";
import { Setting, SettingRow } from "./styles";

type AccessLevelSetting = Extract<
  keyof UserSettings,
  "internalParticipantAccessLevel" | "externalParticipantAccessLevel"
>;

const MeetingReportAccessSettings = () => {
  const { user } = useAuth();
  const { userSettings, updateUserSettings } = useUserSettings();

  const canEditInternalParticipantAccessLevel =
    UserSettingsUtils.canEditInternalParticipantAccessLevel(user);
  const enabledInternalParticipantAccessLevels =
    UserSettingsUtils.enabledInternalParticipantAccessLevelsForUser(user);

  const enabledExternalParticipantAccessLevels =
    UserSettingsUtils.enabledExternalParticipantAccessLevelsForUser(user);

  const externalParticipantAccessLevelDisabledOpts =
    UserSettingsUtils.getSettingDisabledOpts({
      settingIsEditable: enabledExternalParticipantAccessLevels.length > 1,
    });

  const handleUpdateAccessLevel = (
    setting: AccessLevelSetting,
    value: ParticipantAccessLevel
  ) => {
    updateUserSettings.mutate(
      { [setting]: value },
      { onSuccess: noop } // don't show toast on success
    );
  };

  return (
    <SettingRow>
      {canEditInternalParticipantAccessLevel && (
        <Setting {...SETTING_COPY.internalParticipantAccessLevel}>
          <Dropdown
            name="internal-participant-access-level-select"
            selectedItem={getSelectedFromOptions(
              PARTICIPANT_ACCESS_OPTIONS.internal,
              userSettings.internalParticipantAccessLevel
            )}
            onChange={(event) =>
              handleUpdateAccessLevel(
                "internalParticipantAccessLevel",
                event.target.value as ParticipantAccessLevel
              )
            }
          >
            {PARTICIPANT_ACCESS_OPTIONS.internal.map(({ value, label }) => {
              if (!enabledInternalParticipantAccessLevels.includes(value)) {
                return null;
              }

              return (
                <DropdownMenuItem key={value} value={value} label={label} />
              );
            })}
          </Dropdown>
        </Setting>
      )}
      <Setting
        className="review-settings__setting"
        {...SETTING_COPY.externalParticipantAccessLevel}
      >
        <TooltipWrapper
          title={externalParticipantAccessLevelDisabledOpts.tooltip}
          show={externalParticipantAccessLevelDisabledOpts.disabled}
          wrapperSx={{ width: "100%" }}
        >
          <Dropdown
            name="external-participant-access-level-select"
            disabled={externalParticipantAccessLevelDisabledOpts.disabled}
            selectedItem={getSelectedFromOptions(
              PARTICIPANT_ACCESS_OPTIONS.external,
              userSettings.externalParticipantAccessLevel
            )}
            onChange={(event) =>
              handleUpdateAccessLevel(
                "externalParticipantAccessLevel",
                event.target.value as ParticipantAccessLevel
              )
            }
            fullWidth
          >
            {PARTICIPANT_ACCESS_OPTIONS.external.map(({ value, label }) => {
              if (!enabledExternalParticipantAccessLevels.includes(value)) {
                return null;
              }

              return (
                <DropdownMenuItem key={value} value={value} label={label} />
              );
            })}
          </Dropdown>
        </TooltipWrapper>
      </Setting>
    </SettingRow>
  );
};

export default MeetingReportAccessSettings;
